import React, { useEffect, useState } from 'react';
import { Container, Card, Button, Form, Alert, Nav, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';

const Login = () => {
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('google');
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Email/Password login states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Forgot password states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetOtp, setResetOtp] = useState('');
  const [resetSecretKey, setResetSecretKey] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetOtpSent, setResetOtpSent] = useState(false);
  const [maskedMobile, setMaskedMobile] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleGoogleLogin = async () => {
    try {
      const response = await authAPI.googleLogin();
      window.location.href = response.data.auth_url;
    } catch (error) {
      console.error('Login error:', error);
      alert('Failed to initiate login');
    }
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authAPI.sendOTP(mobile);
      if (response.data.success) {
        setSecretKey(response.data.data.result.secret_key);
        setOtpSent(true);
        setError('');
      } else {
        setError(response.data.message || 'Failed to send OTP');
      }
    } catch (error) {
      console.error('Send OTP error:', error);
      setError(error.response?.data?.detail || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authAPI.verifyOTP(mobile, secretKey, otp);
      if (response.data.success && response.data.token) {
        login(response.data.token);
        navigate('/');
      } else {
        setError(response.data.message || 'Invalid OTP. Please try again.');
      }
    } catch (error) {
      console.error('Verify OTP error:', error);
      setError(error.response?.data?.detail || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setOtp('');
    setError('');
    setLoading(true);

    try {
      const response = await authAPI.sendOTP(mobile);
      if (response.data.success) {
        setSecretKey(response.data.data.result.secret_key);
        setError('');
        alert('OTP resent successfully');
      } else {
        setError(response.data.message || 'Failed to resend OTP');
      }
    } catch (error) {
      console.error('Resend OTP error:', error);
      setError(error.response?.data?.detail || 'Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToMobile = () => {
    setOtpSent(false);
    setOtp('');
    setSecretKey('');
    setError('');
  };

  const handleEmailPasswordLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authAPI.emailPasswordLogin(email, password);
      if (response.data.success && response.data.token) {
        login(response.data.token);
        navigate('/');
      } else {
        setError(response.data.message || 'Login failed. Please try again.');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error.response?.data?.detail || 'Invalid email or password.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    setShowForgotPassword(true);
    setError('');
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setResetOtpSent(false);
    setForgotEmail('');
    setResetOtp('');
    setResetSecretKey('');
    setNewPassword('');
    setConfirmPassword('');
    setMaskedMobile('');
    setError('');
  };

  const handleSendResetOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authAPI.forgotPassword(forgotEmail);
      if (response.data.success) {
        setResetSecretKey(response.data.data?.secret_key || '');
        setMaskedMobile(response.data.data?.mobile || '');
        setResetOtpSent(true);
        setError('');
      } else {
        setError(response.data.message || 'Failed to send OTP');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setError(error.response?.data?.detail || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);

    try {
      const response = await authAPI.resetPassword(forgotEmail, resetSecretKey, resetOtp, newPassword);
      if (response.data.success) {
        alert('Password reset successful! You can now login with your new password.');
        handleBackToLogin();
      } else {
        setError(response.data.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setError(error.response?.data?.detail || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
      <Card style={{ width: '450px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <Card.Body className="p-5">
          <div className="text-center mb-4">
            <h2 style={{ color: '#635bff', fontWeight: 'bold' }}>HRMS Portal</h2>
          </div>

          {!showForgotPassword && (
            <Nav variant="tabs" className="mb-4" activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
              <Nav.Item>
                <Nav.Link eventKey="google">Google</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="email">Email</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="mobile">Mobile</Nav.Link>
              </Nav.Item>
            </Nav>
          )}

          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {showForgotPassword ? (
            <>
              {!resetOtpSent ? (
                <Form onSubmit={handleSendResetOTP}>
                  <div className="text-center mb-4">
                    <h5>Forgot Password</h5>
                    <p className="text-muted">Enter your email to receive OTP</p>
                  </div>

                  <Form.Group className="mb-3">
                    <Form.Label>Email Address</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Enter your email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </Form.Group>

                  <Button
                    variant="primary"
                    type="submit"
                    size="lg"
                    className="w-100 mb-2"
                    disabled={loading || !forgotEmail}
                    style={{ padding: '12px' }}
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Sending OTP...
                      </>
                    ) : (
                      'Send OTP'
                    )}
                  </Button>

                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="w-100"
                    onClick={handleBackToLogin}
                    disabled={loading}
                  >
                    Back to Login
                  </Button>
                </Form>
              ) : (
                <Form onSubmit={handleResetPassword}>
                  <div className="text-center mb-4">
                    <h5>Reset Password</h5>
                    <p className="text-muted">
                      OTP sent to mobile ending with {maskedMobile}
                    </p>
                  </div>

                  <Form.Group className="mb-3">
                    <Form.Label>Enter OTP</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 4-digit OTP"
                      value={resetOtp}
                      onChange={(e) => setResetOtp(e.target.value)}
                      required
                      pattern="[0-9]{4}"
                      maxLength="4"
                      disabled={loading}
                      autoFocus
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>New Password</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength="6"
                      disabled={loading}
                    />
                    <Form.Text className="text-muted">
                      Password must be at least 6 characters long
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Confirm Password</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength="6"
                      disabled={loading}
                    />
                  </Form.Group>

                  <Button
                    variant="primary"
                    type="submit"
                    size="lg"
                    className="w-100 mb-2"
                    disabled={loading || resetOtp.length !== 4 || !newPassword || !confirmPassword}
                    style={{ padding: '12px' }}
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Resetting Password...
                      </>
                    ) : (
                      'Reset Password'
                    )}
                  </Button>

                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="w-100"
                    onClick={handleBackToLogin}
                    disabled={loading}
                  >
                    Back to Login
                  </Button>
                </Form>
              )}
            </>
          ) : (
            <>
              {activeTab === 'google' && (
                <>
                  <div className="text-center mb-4">
                    <p className="text-muted">Sign in with your company Google account</p>
                  </div>

                  <Button
                    variant="primary"
                    size="lg"
                    className="w-100"
                    onClick={handleGoogleLogin}
                    style={{ padding: '12px' }}
                  >
                    <svg style={{ marginRight: '10px', width: '20px', height: '20px' }} viewBox="0 0 48 48">
                      <path fill="#FFF" d="M44.5 20H24v8.5h11.8C34.7 33.9 30.1 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22c11 0 21-8 21-22 0-1.3-.2-2.7-.5-4z"/>
                    </svg>
                    Sign in with Google
                  </Button>
                </>
              )}

              {activeTab === 'email' && (
                <Form onSubmit={handleEmailPasswordLogin}>
                  <div className="text-center mb-4">
                    <p className="text-muted">Login with your email and password</p>
                  </div>

                  <Form.Group className="mb-3">
                    <Form.Label>Email Address</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Password</Form.Label>
                    <div className="position-relative">
                      <Form.Control
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        disabled={loading}
                      />
                      <Button
                        variant="link"
                        className="position-absolute end-0 top-0"
                        style={{ textDecoration: 'none', zIndex: 10 }}
                        onClick={() => setShowPassword(!showPassword)}
                        tabIndex="-1"
                      >
                        {showPassword ? '👁️' : '👁️‍🗨️'}
                      </Button>
                    </div>
                  </Form.Group>

                  <div className="text-end mb-3">
                    <Button
                      variant="link"
                      size="sm"
                      onClick={handleForgotPassword}
                      className="p-0"
                      style={{ textDecoration: 'none' }}
                    >
                      Forgot Password?
                    </Button>
                  </div>

                  <Button
                    variant="primary"
                    type="submit"
                    size="lg"
                    className="w-100"
                    disabled={loading || !email || !password}
                    style={{ padding: '12px' }}
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Signing in...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                </Form>
              )}

              {activeTab === 'mobile' && (
            <>
              {!otpSent ? (
                <Form onSubmit={handleSendOTP}>
                  <div className="text-center mb-4">
                    <p className="text-muted">Login with your registered mobile number</p>
                  </div>

                  <Form.Group className="mb-3">
                    <Form.Label>Mobile Number</Form.Label>
                    <Form.Control
                      type="tel"
                      placeholder="Enter 10-digit mobile number"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value)}
                      required
                      pattern="[0-9]{10}"
                      maxLength="10"
                      disabled={loading}
                    />
                    <Form.Text className="text-muted">
                      Enter your 10-digit mobile number
                    </Form.Text>
                  </Form.Group>

                  <Button
                    variant="primary"
                    type="submit"
                    size="lg"
                    className="w-100"
                    disabled={loading || mobile.length !== 10}
                    style={{ padding: '12px' }}
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Sending OTP...
                      </>
                    ) : (
                      'Send OTP'
                    )}
                  </Button>
                </Form>
              ) : (
                <Form onSubmit={handleVerifyOTP}>
                  <div className="text-center mb-4">
                    <p className="text-muted">
                      OTP sent to {mobile}
                      <br />
                      <Button
                        variant="link"
                        size="sm"
                        onClick={handleBackToMobile}
                        className="p-0"
                      >
                        Change number
                      </Button>
                    </p>
                  </div>

                  <Form.Group className="mb-3">
                    <Form.Label>Enter OTP</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 4-digit OTP"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      required
                      pattern="[0-9]{4}"
                      maxLength="4"
                      disabled={loading}
                      autoFocus
                    />
                  </Form.Group>

                  <Button
                    variant="primary"
                    type="submit"
                    size="lg"
                    className="w-100 mb-2"
                    disabled={loading || otp.length !== 4}
                    style={{ padding: '12px' }}
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Verifying...
                      </>
                    ) : (
                      'Verify OTP'
                    )}
                  </Button>

                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="w-100"
                    onClick={handleResendOTP}
                    disabled={loading}
                  >
                    Resend OTP
                  </Button>
                </Form>
              )}
            </>
              )}
            </>
          )}

          <div className="mt-4 text-center">
            <small className="text-muted">
              By signing in, you agree to our terms and privacy policy
            </small>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Login;
